import type React from 'react';
import { useIntl } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import {
  XING_JOBS_FIND_PATH,
  XING_JOBS_MY_JOBS_PATH,
} from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  ORIGIN_PARAM,
  ORIGIN_PARAM_VALUES,
} from '@xing-com/crate-jobs-constants';
import { TopBar } from '@xing-com/crate-layout-top-bar';
import {
  mediaTopBarHidden,
  mediaTopBarVisible,
  zIndexElevatedLayer4,
} from '@xing-com/layout-tokens';
import { xdlColorXING } from '@xing-com/tokens';

import { FilterBar } from '../filters';

import { FakeInput } from './fake-input';
import { getPrimaryActions } from './primary-actions';
import { SmallScreenHeaderLoggedOut } from './small-screnn-header-logged-out';

type Props = {
  isSticky: boolean;
};

export const Header: React.FC<Props> = ({ isSticky }) => {
  const { formatMessage } = useIntl();
  const { isLoggedIn } = useLoginState();
  const isSmallScreen = useMatchMedia(mediaTopBarVisible);
  const { getSearchParameter } = useSearchParameter();

  const findJobsTitle = formatMessage({ id: 'JOBS_FIND_JOBS_TITLE' });
  const searchAlertsTitle = formatMessage({
    id: 'JOBS_MYJOBS_SEARCH_ALERTS_SECTION_TITLE',
  });

  const fromSearchAlerts =
    getSearchParameter(ORIGIN_PARAM) === ORIGIN_PARAM_VALUES.searchAlerts;
  const backButtonUrl = fromSearchAlerts
    ? `${XING_JOBS_MY_JOBS_PATH}?active_view=SEARCH_ALERTS`
    : XING_JOBS_FIND_PATH;
  const backButtonUrlAriaLabelSection = fromSearchAlerts
    ? searchAlertsTitle
    : findJobsTitle;

  return isLoggedIn ? (
    <>
      <StyledTopBar
        type="subheaderSearchResults"
        showOnLargerScreens={isSticky}
        backButtonUrl={backButtonUrl}
        backButtonUrlAriaLabelSection={backButtonUrlAriaLabelSection}
        primaryActions={getPrimaryActions()}
        subHeaderContent={!isSmallScreen && isSticky ? <FakeInput /> : null}
        // trick to show the subHeaderContent on larger screens (LI)
        isLoggedOut={!isSmallScreen && isSticky}
        isSticky={isSticky}
        title={findJobsTitle}
        hidePlacebo={!isSmallScreen}
      />
      <FixedFilterBar $isSticky={isSticky} />
    </>
  ) : (
    <>
      <SmallScreenHeaderLoggedOut />
      {isSticky ? (
        // trick to show the subHeaderContent on larger screens (LO)
        <StyledTopBar
          type="subheaderSearchResults"
          showOnLargerScreens
          subHeaderContent={<FakeInput />}
          isLoggedOut
          isSticky
          skipAdditionalButtons
          hidePlacebo
        />
      ) : null}
    </>
  );
};

const animation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const animationContent = css`
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: ${xdlColorXING};
    animation: ${animation} 0.2s forwards;
    pointer-events: none;
  }
`;

const StyledTopBar = styled(TopBar)`
  ${({ isSticky }) =>
    isSticky &&
    css`
      z-index: calc(${zIndexElevatedLayer4} + 50);
      ${animationContent}
    `}

  ${({ isLoggedOut }) =>
    isLoggedOut &&
    css`
      margin-top: 54px;

      @media ${mediaTopBarHidden} {
        margin-top: 84px;
      }

      & a {
        display: none;
      }
    `}
`;

const FixedFilterBar = styled(FilterBar)<{ $isSticky: boolean }>`
  display: none;

  @media ${mediaTopBarVisible} {
    ${({ $isSticky }) =>
      $isSticky &&
      css`
        display: flex;
        position: fixed;
        background-color: ${xdlColorXING};
        z-index: ${zIndexElevatedLayer4};
        width: 100%;

        ${animationContent}
      `}
  }
`;
